import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath, useLocation } from 'react-router-dom'
import {
  getInsurancePresignedUploadUrl,
  patchAcceptRejectDecodedPlateVIN,
  uploadInsuranceCardToPresignedUrl,
  verifyInsuranceCard,
  verifyPresignedInsuranceCard,
} from 'actions/vehicles'
import { DrivingProfile, ProfileVehicle } from 'types/profile'
import { ModalOptions } from 'types/global'
import { useInsurancePollingContext } from 'context/InsurancePollingContext'
import { useLocalizedTranslations } from 'localization'
import type { TranslationKey } from 'localization'

type UseInsuranceSubmissionViewModelProps = {
  vehicle_id: number
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
  fromFailureModal: boolean
  openModalOnTryAgain: boolean | null
  useS3UploadFlow: boolean
}

const insuranceUploadFailureMessageKeys: Record<string, TranslationKey> = {
  prepare: 'dweb_flash_insurance_prepare_upload_issue_try_again_later',
  upload: 'dweb_flash_insurance_upload_issue_try_again_later',
  verify: 'dweb_flash_insurance_submit_review_issue_try_again_later',
  fallback: 'dweb_flash_insurance_upload_issue_try_again_later',
}

const insuranceCardRequirementKeys = {
  nameMatchesProfile: 'dweb_account_driving_info_vehicle_info_insurance_submission_name_matches_profile',
  vehicleIdentificationNumber: 'dweb_insurance_submission_vin_requirement',
  expirationDate: 'dweb_account_driving_info_vehicle_info_insurance_submission_expiration_date',
} as const

export const useInsuranceSubmissionViewModel = (props: UseInsuranceSubmissionViewModelProps) => {
  const { setModalOptions, useS3UploadFlow, vehicle_id } = props
  const { startPolling, stopPolling } = useInsurancePollingContext()
  const dispatch = useDispatch()
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const vehicles = useSelector<any, ProfileVehicle[]>((state) => state.vehicles)

  const vehicle = vehicles.find((v) => v.details.id === vehicle_id)

  const [file, setFile] = useState<File>()
  const [photoKey, setPhotoKey] = useState<number>(0)

  const insuranceCardRequirementText = useLocalizedTranslations(insuranceCardRequirementKeys)
  const insuranceCardReqs = [
    insuranceCardRequirementText.nameMatchesProfile,
    insuranceCardRequirementText.vehicleIdentificationNumber,
    insuranceCardRequirementText.expirationDate,
  ]

  const handleSubmit = useCallback(async () => {
    let failurePoint: keyof typeof insuranceUploadFailureMessageKeys = 'fallback'

    try {
      setModalOptions?.({ modalOpen: false, modalType: null })
      if (vehicle?.plate_lookup?.result === 'pending') {
        await patchAcceptRejectDecodedPlateVIN(profile.id, vehicle_id, 'reject')(dispatch)
      }

      if (useS3UploadFlow) {
        failurePoint = 'prepare'
        const presignedUploadUrl = await getInsurancePresignedUploadUrl(vehicle_id)

        failurePoint = 'upload'
        await uploadInsuranceCardToPresignedUrl(presignedUploadUrl, file)

        failurePoint = 'verify'
        const verifyPromise = verifyPresignedInsuranceCard(vehicle_id)
        startPolling(vehicle_id)
        await verifyPromise
      } else {
        startPolling(vehicle_id)
        await verifyInsuranceCard(file, profile.id.toString(), vehicle_id)
      }
    } catch (err) {
      setModalOptions?.({ modalOpen: false, modalType: null })
      dispatch({
        type: 'PUSH_FLASH',
        flash: { messageKey: insuranceUploadFailureMessageKeys[failurePoint], type: 'alert' },
      })
      stopPolling('idle')
    }
  }, [dispatch, file, profile.id, setModalOptions, startPolling, stopPolling, useS3UploadFlow, vehicle, vehicle_id])

  const handleChange = useCallback(
    (res: { file: File }) => {
      if (res) {
        setFile(res.file)
        setModalOptions?.({ modalType: 'insurance-card', modalOpen: true })
      }
    },
    [setModalOptions]
  )

  const handlePhotoError = useCallback(() => {
    dispatch({
      type: 'PUSH_FLASH',
      flash: {
        messageKey: 'dweb_flash_insurance_processing_issue_try_smaller_clearer_photo',
        type: 'alert',
      },
    })
  }, [dispatch])

  const resetPhotoForm = useCallback(() => {
    setFile(undefined)
    setPhotoKey((prevKey) => prevKey + 1)
    setModalOptions?.({ modalOpen: false, modalType: null })
  }, [setModalOptions])

  return {
    isWebView,
    file,
    photoKey,
    insuranceCardReqs,
    handleSubmit,
    handleChange,
    handlePhotoError,
    resetPhotoForm,
  }
}

export const useInsuranceFailureViewModel = (setModalOptions: React.Dispatch<React.SetStateAction<ModalOptions>>) => {
  const { status, vehicleId, attemptCount, response } = useInsurancePollingContext()
  const location = useLocation()
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)

  const openModalOnTryAgain = Boolean(
    matchPath(location.pathname, { path: `/account/driving-info/vehicle-info/${vehicleId}` }) ||
      matchPath(location.pathname, { path: `/account/driving-info/new-vehicle` })
  )

  useEffect(() => {
    if (status == 'rejected' && response?.plate_vin_mismatch != true) {
      setModalOptions({ modalOpen: true, modalType: 'insurance-failure' })
    } else {
      setModalOptions({ modalOpen: false, modalType: null })
    }
  }, [status, setModalOptions, response])

  return {
    isWebView,
    vehicleId,
    attemptCount,
    response,
    openModalOnTryAgain,
  }
}
